import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { css } from '@emotion/react';

const pageStyles = css`
  min-height: 100vh;
  background: #fff;
  font-family: 'FlodeskSans', sans-serif;
`;

/* ── Top Nav ── */

const topNavStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 56px;
  background: #fff;
  border-bottom: 1px solid rgba(55, 49, 47, 0.12);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 36px;
  z-index: 10;
`;

const logoStyles = css`
  width: 63px;
  height: 13px;
`;

const stepsNavStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-weight: 500;
`;

const stepInactiveStyles = css`
  color: rgba(61, 46, 41, 0.38);
`;

const stepActiveStyles = css`
  color: #1f1714;
`;

const stepSeparatorStyles = css`
  color: rgba(61, 46, 41, 0.38);
  font-family: 'Beausite Classic', serif;
  font-weight: 400;
`;

const nextLinkStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(61, 46, 41, 0.52);
  font-size: 14px;
  width: 120px;
  justify-content: flex-end;
  cursor: pointer;
`;

/* ── Secondary Nav ── */

const secondaryNavStyles = css`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 40px;
  z-index: 9;
  background: #fff;
`;

const backStyles = css`
  font-size: 14px;
  color: rgba(61, 46, 41, 0.52);
  cursor: pointer;
  background: none;
  border: none;
  font-family: 'FlodeskSans', sans-serif;
  padding: 0;

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }
`;

const continueButtonStyles = css`
  background: #241a17;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 18px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    background: #3a2d28;
  }
`;

/* ── Main Content ── */

const contentAreaStyles = css`
  display: flex;
  gap: 80px;
  align-items: flex-start;
  justify-content: center;
  padding-top: 236px;
  padding-bottom: 80px;
`;

const emailPreviewStyles = css`
  width: 338px;
  height: 446px;
  border-radius: 10px;
  border: 1px solid #f3f2f1;
  overflow-y: auto;
  box-shadow:
    0 5.88px 14.05px -1.94px rgba(0, 0, 0, 0.09),
    0 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
    0 1px 1px -0.1px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const formSectionStyles = css`
  width: 336px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

/* ── Steps + Title ── */

const stepsTitleStyles = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const dotsStyles = css`
  display: flex;
  gap: 6px;
`;

const dotStyles = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
`;

const dotActiveStyles = css`
  background: #241a17;
`;

const dotInactiveStyles = css`
  background: rgba(61, 46, 41, 0.2);
`;

const headingStyles = css`
  font-size: 30px;
  font-weight: 500;
  line-height: 34px;
  color: #241a17;
  margin: 0;
`;

/* ── Step 1: Sender Fields ── */

const fieldsStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const fieldStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  padding: 10px 24px;
  height: 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const fieldLabelStyles = css`
  font-size: 12px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 17.8px;
  margin: 0;
`;

const fieldValueStyles = css`
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  margin: 0;
`;

const selectFieldWrapperStyles = css`
  position: relative;
`;

const selectFieldStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
  background: none;
  font-family: 'FlodeskSans', sans-serif;

  &:hover {
    border-color: rgba(61, 46, 41, 0.4);
  }
`;

const chevronStyles = css`
  width: 10px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 10px;
    height: auto;
  }
`;

const dropdownStyles = css`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0 5.88px 14.05px rgba(0, 0, 0, 0.09),
    0 1.98px 3.06px rgba(0, 0, 0, 0.05),
    0 1px 1px rgba(0, 0, 0, 0.05);
  padding: 8px;
  z-index: 20;
`;

const dropdownItemStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  font-family: 'FlodeskSans', sans-serif;
  text-align: left;

  &:hover {
    background: rgba(61, 46, 41, 0.06);
  }
`;

const dropdownItemLabelStyles = css`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const checkIconStyles = css`
  width: 16px;
  height: 11px;
  flex-shrink: 0;
`;

const helperTextStyles = css`
  font-size: 12px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 17.8px;
  padding: 0 8px;
  margin: 0;

  a {
    color: rgba(61, 46, 41, 0.52);
    text-decoration: underline;
  }
`;

/* ── Step 2: Subject Line Fields ── */

const sectionLabelStyles = css`
  font-size: 14px;
  font-weight: 500;
  color: #241a17;
  line-height: 20px;
  margin: 0;
`;

const sectionLabelRowStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const infoIconStyles = css`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
`;

const subjectInputStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: #fff;
  margin-top: 16px;

  input {
    border: none;
    outline: none;
    font-family: 'FlodeskSans', sans-serif;
    font-size: 14px;
    color: #241a17;
    line-height: 20px;
    flex: 1;
    background: transparent;
  }
`;

const emojiButtonStyles = css`
  width: 13px;
  height: 13px;
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const previewTextWrapperStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  padding: 20px 24px;
  height: 128px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  background: #fff;

  textarea {
    border: none;
    outline: none;
    font-family: 'FlodeskSans', sans-serif;
    font-size: 14px;
    color: #241a17;
    line-height: 20px;
    resize: none;
    flex: 1;
    background: transparent;
    width: 100%;
  }
`;

const charCountStyles = css`
  font-size: 12px;
  line-height: 17.8px;
  text-align: right;
  margin: 0;

  span:first-of-type {
    color: rgba(61, 46, 41, 0.52);
  }
  span:last-of-type {
    color: #241a17;
  }
`;

const addAbTestStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: rgba(57, 48, 45, 0.52);
  line-height: 20px;
  text-align: right;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  align-self: flex-end;

  &:hover {
    color: rgba(57, 48, 45, 0.72);
  }
`;

const continueButtonDisabledStyles = css`
  background: none;
  color: #887f7c;
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 6px;
  padding: 12px 18px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: default;
`;

/* ── Step 3: A/B Test Fields ── */

const subjectBFieldStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  height: 60px;
  display: flex;
  align-items: center;
  width: 100%;
  background: #fff;
  position: relative;
  cursor: text;
`;

const subjectBInputAreaStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  padding: 0 24px;
  height: 100%;

  input {
    border: none;
    outline: none;
    font-family: 'FlodeskSans', sans-serif;
    font-size: 13.5px;
    color: #241a17;
    line-height: 19.3px;
    flex: 1;
    background: transparent;

    &::placeholder {
      color: rgba(61, 46, 41, 0.52);
      transition: opacity 0.15s ease;
    }

    &:focus::placeholder {
      opacity: 0;
    }
  }
`;

const subjectBDividerStyles = css`
  width: 1px;
  height: 58px;
  background: rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
`;

const closeButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 100%;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  flex-shrink: 0;

  img {
    width: 10px;
    height: 10px;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const abRevealStyles = css`
  display: grid;
  grid-template-rows: 0fr;
  transition:
    grid-template-rows 0.35s ease,
    margin-top 0.35s ease;
  overflow: hidden;
  margin-top: 0;

  &[data-open='true'] {
    grid-template-rows: 1fr;
    margin-top: 16px;
  }
`;

const abRevealInnerStyles = css`
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const abHelperFadeStyles = css`
  opacity: 0;
  height: 0;
  overflow: hidden;
  transition:
    opacity 0.3s ease,
    height 0.35s ease,
    margin-top 0.35s ease;
  margin-top: 0;

  &[data-visible='true'] {
    opacity: 1;
    height: auto;
    overflow: visible;
    margin-top: 16px;
  }
`;

const abHelperTextStyles = css`
  font-size: 12px;
  color: rgba(57, 48, 45, 0.52);
  line-height: 17.8px;
  margin: 0;

  a {
    color: rgba(57, 48, 45, 0.52);
    text-decoration: underline;
  }
`;

const step2FieldsStyles = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const subjectFieldGroupStyles = css`
  display: flex;
  flex-direction: column;
`;

const fieldGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SUBJECT_B_SUGGESTIONS = [
  'Big savings, limited time',
  'Save big in our flash sale',
  'Take 50% off your favorites',
];

const subjectBWrapperStyles = css`
  position: relative;
`;

const suggestionsDropdownStyles = css`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0 5.88px 14.05px rgba(0, 0, 0, 0.09),
    0 1.98px 3.06px rgba(0, 0, 0, 0.05),
    0 1px 1px rgba(0, 0, 0, 0.05);
  padding: 8px;
  z-index: 20;
`;

const suggestionItemStyles = css`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  font-family: 'FlodeskSans', sans-serif;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    background: rgba(61, 46, 41, 0.06);
  }
`;

/* ── Step 4: Choose Recipients ── */

const recipientButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 42px;
  padding: 0 24px;
  border: 1px solid rgba(55, 49, 47, 0.2);
  border-radius: 6px;
  background: #fff;
  font-family: 'Beausite Classic', serif;
  font-size: 15px;
  color: #241a17;
  letter-spacing: 0.15px;
  cursor: pointer;
  width: fit-content;

  &:hover {
    border-color: rgba(55, 49, 47, 0.4);
  }
`;

const uploadButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 42px;
  padding: 0 24px;
  border: 1px dashed rgba(55, 49, 47, 0.2);
  border-radius: 6px;
  background: #fff;
  font-family: 'Beausite Classic', serif;
  font-size: 15px;
  color: #241a17;
  letter-spacing: 0.15px;
  cursor: pointer;
  width: fit-content;

  img {
    width: 12px;
    height: 13px;
  }

  &:hover {
    border-color: rgba(55, 49, 47, 0.4);
  }
`;

const recipientsGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
`;

const includedLabelStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #241a17;
  line-height: 20px;
  margin: 0;
`;

const includedBadgesStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const includedBadgeStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 32px;
  padding: 0 12px;
  border-radius: 3px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: #241a17;
  line-height: 17.8px;
  white-space: nowrap;
  width: fit-content;
`;

const addMoreButtonStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 62px;
  padding: 8px 0;
  border: 1px solid rgba(55, 49, 47, 0.2);
  border-radius: 6px;
  background: #fff;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: #241a17;
  line-height: 17.8px;
  cursor: pointer;

  &:hover {
    border-color: rgba(55, 49, 47, 0.4);
  }
`;

const recipientsChosenStyles = css`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const includedSectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/* ── Step 5: Send ── */

const sendOptionStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 82px;
  padding: 12px 24px;
  border: 1px solid rgba(55, 49, 47, 0.2);
  border-radius: 6px;
  background: #fff;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    border-color: rgba(55, 49, 47, 0.4);
  }

  &[data-selected='true'] {
    border-color: #241a17;
  }
`;

const sendOptionsGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const sendContentGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const sendExplanationStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  margin: 0;
  width: 303px;
`;

const sendNowButtonStyles = css`
  background: #241a17;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  width: fit-content;

  &:hover {
    background: #3a2d28;
  }
`;

const sendTestStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 3px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 20px;

  img {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }
`;

/* ── Confirmation Modals ── */

const modalBackdropStyles = css`
  position: fixed;
  inset: 0;
  background: rgba(29, 23, 22, 0.59);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const modalCardStyles = css`
  background: #fff;
  border-radius: 10px;
  box-shadow:
    0 23.29px 85.96px rgba(0, 0, 0, 0.19),
    0 5.88px 14.05px rgba(0, 0, 0, 0.09),
    0 1.98px 3.06px rgba(0, 0, 0, 0.05),
    0 1px 1px rgba(0, 0, 0, 0.05);
  width: 603px;
  padding: 56px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 48px;
`;

const modalTitleStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 30px;
  font-weight: 500;
  line-height: 34px;
  color: #241a17;
  text-align: center;
  margin: 0;
`;

const modalBodyStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: #241a17;
  line-height: 20px;
  text-align: center;
  margin: 0;
  width: 459px;
`;

const modalBodyBoldStyles = css`
  font-weight: 500;
`;

const modalActionsStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const modalPrimaryStyles = css`
  background: #241a17;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  &:hover {
    background: #3a2d28;
  }
`;

const modalSecondaryStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 20px;
  text-align: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }
`;

/* ── Segments Overlay ── */

const overlayStyles = css`
  position: fixed;
  inset: 0;
  background: #fff;
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const overlayCloseStyles = css`
  position: absolute;
  top: 48px;
  right: 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const overlayCloseXStyles = css`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 16px;
    height: 16px;
  }
`;

const overlayCloseEscStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 20px;
`;

const overlayTitleStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 30px;
  font-weight: 500;
  line-height: 34px;
  color: #241a17;
  text-align: center;
  margin: 213px 0 0;
`;

const overlayContentStyles = css`
  width: 480px;
  margin-top: 60px;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const overlayFieldGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const overlayFieldLabelStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #241a17;
  line-height: 20px;
  margin: 0;
`;

const overlayInputWrapperStyles = css`
  position: relative;
`;

const overlayInputStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: #fff;
  transition: border-color 0.15s ease;

  &[data-focused='true'] {
    border-color: #3373e1;
  }

  input {
    border: none;
    outline: none;
    font-family: 'FlodeskSans', sans-serif;
    font-size: 14px;
    color: #241a17;
    line-height: 20px;
    flex: 1;
    background: transparent;

    &::placeholder {
      color: rgba(61, 46, 41, 0.52);
    }
  }
`;

const segmentDropdownStyles = css`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow:
    0 5.88px 14.05px -1.94px rgba(0, 0, 0, 0.09),
    0 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05),
    0 1px 1px -0.1px rgba(0, 0, 0, 0.05);
  padding: 24px;
  z-index: 20;
  max-height: 186px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const segmentRowStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const segmentBadgeStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 3px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: #241a17;
  line-height: 17.8px;
  white-space: nowrap;
`;

const segmentCountStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 17.8px;
`;

const selectedBadgesStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const selectedBadgeStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  height: 32px;
  padding: 0 12px;
  border-radius: 3px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: #241a17;
  line-height: 17.8px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const badgeRemoveStyles = css`
  width: 7px;
  height: 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    width: 7px;
    height: 7px;
  }

  &:hover {
    opacity: 0.6;
  }
`;

const clearButtonStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 17.8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:hover {
    color: rgba(61, 46, 41, 0.72);
  }
`;

const includeFieldRightStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: flex-end;
  flex-shrink: 0;
`;

const overlayInputSelectedStyles = css`
  border: 1px solid rgba(61, 46, 41, 0.23);
  border-radius: 12px;
  padding: 0 16px 0 16px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: #fff;
  gap: 8px;

  &[data-focused='true'] {
    border-color: #3373e1;
  }
`;

const SEGMENTS = [
  { name: 'All active subscribers', count: 1800, color: '#f3f2f1' },
  { name: 'Mailchimp imports', count: 11, color: '#fff96c' },
  { name: 'New mailchimp imports', count: 651, color: '#f5ede4' },
  { name: 'New subscribers', count: 145, color: '#e2c2f9' },
  { name: 'February promotion', count: 50, color: '#e0e7c6' },
];

const overlayExcludeGroupStyles = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const overlayHelperStyles = css`
  font-family: 'FlodeskSans', sans-serif;
  font-size: 12px;
  color: rgba(61, 46, 41, 0.52);
  line-height: 17.8px;
  padding: 0 8px;
  margin: 0;
`;

const overlaySaveStyles = css`
  background: #241a17;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 12px 24px;
  font-family: 'FlodeskSans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  align-self: flex-end;
  margin-top: 16px;

  &:hover {
    background: #3a2d28;
  }
`;

const EMAIL_OPTIONS = ['daniele@flodesk.com', 'sales@flodesk.com', 'info@flodesk.com'];

export default function SendFlow() {
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState('daniele@flodesk.com');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [subjectLine, setSubjectLine] = useState('Flash sales are here ⚡️');
  const [subjectLineB, setSubjectLineB] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const subjectBInputRef = useRef<HTMLInputElement>(null);
  const [previewText, setPreviewText] = useState(
    'Fall in love with our new collection at half the price. Shop now!',
  );
  const [sendChoice, setSendChoice] = useState<'now' | 'later' | null>(null);
  const [confirmModal, setConfirmModal] = useState<'check' | 'woohoo' | null>(null);
  const [segmentsOpen, setSegmentsOpen] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [includeDropdownOpen, setIncludeDropdownOpen] = useState(false);
  const includeRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setSuggestionsOpen(false);
      }
      if (includeRef.current && !includeRef.current.contains(e.target as Node)) {
        setIncludeDropdownOpen(false);
      }
    }
    if (dropdownOpen || suggestionsOpen || includeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen, suggestionsOpen, includeDropdownOpen]);

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setSegmentsOpen(false);
    }
    if (segmentsOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    return () => document.removeEventListener('keydown', handleEsc);
  }, [segmentsOpen]);

  return (
    <div css={pageStyles}>
      {/* Top Navigation */}
      <nav css={topNavStyles}>
        <div
          css={css`
            width: 120px;
          `}
        >
          <img css={logoStyles} src="/flodesk-logo.svg" alt="Flodesk" />
        </div>
        <div css={stepsNavStyles}>
          <span css={stepInactiveStyles}>Choose Template</span>
          <span css={stepSeparatorStyles}>&gt;</span>
          <span css={step === 5 ? stepActiveStyles : stepInactiveStyles}>Design Email</span>
          <span css={stepSeparatorStyles}>&gt;</span>
          <span css={stepActiveStyles}>Choose Audience</span>
          <span css={stepSeparatorStyles}>&gt;</span>
          <span css={stepInactiveStyles}>Send</span>
        </div>
        <div css={nextLinkStyles}>
          <span>Next</span>
          <span
            css={css`
              font-size: 11px;
            `}
          >
            &gt;
          </span>
        </div>
      </nav>

      {/* Secondary Navigation */}
      <div css={secondaryNavStyles}>
        <button
          type="button"
          css={backStyles}
          onClick={() => {
            if (step === 5) setStep(4);
            else if (step === 4) setStep(subjectLineB ? 3 : 2);
            else if (step === 3) setStep(2);
            else if (step > 1) setStep(step - 1);
          }}
        >
          &lt; Back
        </button>
        {step !== 5 ? (
          <button
            type="button"
            css={
              (step === 3 && !subjectLineB) || (step === 4 && selectedSegments.length === 0)
                ? continueButtonDisabledStyles
                : continueButtonStyles
            }
            onClick={() => {
              if (step === 4 && selectedSegments.length === 0) return;
              if (step === 3 && !subjectLineB) return;
              const nextStepMap: Record<number, number> = { 1: 2, 2: 4, 3: 4 };
              setStep(nextStepMap[step] ?? step + 1);
            }}
          >
            Continue
          </button>
        ) : (
          <button type="button" css={sendTestStyles}>
            <img src="/send-icon.svg" alt="" />
            Send a test
          </button>
        )}
      </div>

      {/* Main Content */}
      <div css={contentAreaStyles}>
        {/* Email Preview */}
        <div css={emailPreviewStyles}>
          <img src="/email-preview.png" alt="Email preview" />
        </div>

        {/* Form Section */}
        <div css={formSectionStyles}>
          <div css={stepsTitleStyles}>
            {step !== 5 && (
              <div css={dotsStyles}>
                <div css={[dotStyles, step === 1 ? dotActiveStyles : dotInactiveStyles]} />
                <div
                  css={[dotStyles, step === 2 || step === 3 ? dotActiveStyles : dotInactiveStyles]}
                />
                <div css={[dotStyles, step === 4 ? dotActiveStyles : dotInactiveStyles]} />
              </div>
            )}
            <h1 css={headingStyles}>
              {step === 1 && "Who's this email coming from?"}
              {(step === 2 || step === 3) && (
                <>
                  Write your
                  <br />
                  subject line
                </>
              )}
              {step === 4 && selectedSegments.length === 0 && (
                <>
                  Choose your
                  <br />
                  recipients
                </>
              )}
              {step === 4 && selectedSegments.length > 0 && (
                <>
                  {selectedSegments.reduce((total, name) => {
                    const seg = SEGMENTS.find(s => s.name === name);
                    return total + (seg?.count || 0);
                  }, 0)}{' '}
                  recipients
                  <br />
                  chosen
                </>
              )}
              {step === 5 && (
                <>
                  When should we
                  <br />
                  send this email?
                </>
              )}
            </h1>
          </div>

          {/* Step 1: Sender Info */}
          {step === 1 && (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 16px;
              `}
            >
              <div css={fieldsStyles}>
                <div css={fieldStyles}>
                  <p css={fieldLabelStyles}>From name</p>
                  <p css={fieldValueStyles}>Daniele</p>
                </div>

                <div css={selectFieldWrapperStyles} ref={dropdownRef}>
                  <button
                    type="button"
                    css={selectFieldStyles}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <p css={fieldValueStyles}>{selectedEmail}</p>
                    <span css={chevronStyles}>
                      <img src="/chevron-down.svg" alt="" />
                    </span>
                  </button>

                  {dropdownOpen && (
                    <div css={dropdownStyles}>
                      {EMAIL_OPTIONS.map(email => (
                        <button
                          type="button"
                          key={email}
                          css={dropdownItemStyles}
                          onClick={() => {
                            setSelectedEmail(email);
                            setDropdownOpen(false);
                          }}
                        >
                          <span css={dropdownItemLabelStyles}>{email}</span>
                          {email === selectedEmail && (
                            <img css={checkIconStyles} src="/check.svg" alt="" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <p css={helperTextStyles}>
                If you&apos;d like to send from a different email address,
                <br />
                manage senders{' '}
                <button
                  type="button"
                  css={css`
                    color: rgba(61, 46, 41, 0.52);
                    text-decoration: underline;
                    background: none;
                    border: none;
                    padding: 0;
                    font: inherit;
                    cursor: pointer;
                  `}
                  onClick={() => {}}
                >
                  here
                </button>
                .
              </p>
            </div>
          )}

          {/* Step 2: Subject Line / Step 3: A/B Test */}
          {(step === 2 || step === 3) && (
            <div css={step2FieldsStyles}>
              <div css={subjectFieldGroupStyles}>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  `}
                >
                  <p css={sectionLabelStyles}>Subject line</p>
                  {step === 2 && (
                    <button type="button" css={addAbTestStyles} onClick={() => setStep(3)}>
                      Run A/B test
                    </button>
                  )}
                </div>
                <div css={subjectInputStyles}>
                  <input
                    type="text"
                    value={subjectLine}
                    onChange={e => setSubjectLine(e.target.value)}
                  />
                  <img css={emojiButtonStyles} src="/emoji-icon.svg" alt="Emoji" />
                </div>
                <div css={subjectBWrapperStyles} ref={suggestionsRef}>
                  <div css={abRevealStyles} data-open={step === 3}>
                    <div css={abRevealInnerStyles}>
                      <div
                        css={subjectBFieldStyles}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter') subjectBInputRef.current?.focus();
                        }}
                        onClick={() => {
                          subjectBInputRef.current?.focus();
                        }}
                      >
                        <div css={subjectBInputAreaStyles}>
                          <input
                            ref={subjectBInputRef}
                            type="text"
                            placeholder="Subject line B"
                            value={subjectLineB}
                            onChange={e => setSubjectLineB(e.target.value)}
                            onFocus={() => setSuggestionsOpen(true)}
                          />
                          <img css={emojiButtonStyles} src="/emoji-icon.svg" alt="Emoji" />
                        </div>
                        <div css={subjectBDividerStyles} />
                        <button type="button" css={closeButtonStyles} onClick={() => setStep(2)}>
                          <img src="/close-icon.svg" alt="Remove" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {suggestionsOpen && step === 3 && (
                    <div css={suggestionsDropdownStyles}>
                      {SUBJECT_B_SUGGESTIONS.map(suggestion => (
                        <button
                          type="button"
                          key={suggestion}
                          css={suggestionItemStyles}
                          onClick={() => {
                            setSubjectLineB(suggestion);
                            setSuggestionsOpen(false);
                          }}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div css={abHelperFadeStyles} data-visible={step === 3}>
                  <p css={abHelperTextStyles}>
                    We test both subject lines with a small group for 4 hours, then send the winner
                    to the rest of your list.{' '}
                    <button
                      type="button"
                      css={css`
                        color: rgba(57, 48, 45, 0.52);
                        text-decoration: underline;
                        background: none;
                        border: none;
                        padding: 0;
                        font: inherit;
                        cursor: pointer;
                      `}
                      onClick={() => {}}
                    >
                      Learn more
                    </button>
                  </p>
                </div>
              </div>

              <div css={fieldGroupStyles}>
                <div css={sectionLabelRowStyles}>
                  <p css={sectionLabelStyles}>Preview text</p>
                  <img css={infoIconStyles} src="/info-icon.svg" alt="Info" />
                </div>
                <div css={previewTextWrapperStyles}>
                  <textarea
                    value={previewText}
                    onChange={e => {
                      if (e.target.value.length <= 90) {
                        setPreviewText(e.target.value);
                      }
                    }}
                  />
                  <p css={charCountStyles}>
                    <span>{previewText.length}/</span>
                    <span>90</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Choose Recipients */}
          {step === 4 && selectedSegments.length === 0 && (
            <div css={recipientsGroupStyles}>
              <button
                type="button"
                css={recipientButtonStyles}
                onClick={() => setSegmentsOpen(true)}
              >
                + Add segments
              </button>
              <button type="button" css={recipientButtonStyles}>
                + Add individuals
              </button>
              <button type="button" css={uploadButtonStyles}>
                <img src="/upload-icon.svg" alt="" />
                Upload CSV
              </button>
            </div>
          )}
          {step === 4 && selectedSegments.length > 0 && (
            <div css={recipientsChosenStyles}>
              <div css={includedSectionStyles}>
                <p css={includedLabelStyles}>Included</p>
                <div css={includedBadgesStyles}>
                  {selectedSegments.map(name => {
                    const seg = SEGMENTS.find(s => s.name === name);
                    return (
                      <span key={name} css={includedBadgeStyles} style={{ background: seg?.color }}>
                        {name}
                        <span
                          css={badgeRemoveStyles}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter')
                              setSelectedSegments(selectedSegments.filter(s => s !== name));
                          }}
                          onClick={() =>
                            setSelectedSegments(selectedSegments.filter(s => s !== name))
                          }
                        >
                          <img src="/badge-close.svg" alt="Remove" />
                        </span>
                      </span>
                    );
                  })}
                </div>
              </div>
              <button type="button" css={addMoreButtonStyles} onClick={() => setSegmentsOpen(true)}>
                + Add
              </button>
            </div>
          )}

          {/* Step 5: Send */}
          {step === 5 && (
            <div css={sendContentGroupStyles}>
              <div css={sendOptionsGroupStyles}>
                <button
                  type="button"
                  css={sendOptionStyles}
                  data-selected={sendChoice === 'now'}
                  onClick={() => setSendChoice('now')}
                >
                  Now
                </button>
                <button
                  type="button"
                  css={sendOptionStyles}
                  data-selected={sendChoice === 'later'}
                  onClick={() => setSendChoice('later')}
                >
                  Later
                </button>
              </div>
              {sendChoice === 'now' && (
                <>
                  <p css={sendExplanationStyles}>
                    The A and B test groups will receive the email immediately. The remaining
                    recipients will receive the winning version in 4 hours.
                  </p>
                  <button
                    type="button"
                    css={sendNowButtonStyles}
                    onClick={() => setConfirmModal('check')}
                  >
                    Send now
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Segments Overlay */}
      {segmentsOpen && (
        <div css={overlayStyles}>
          <button type="button" css={overlayCloseStyles} onClick={() => setSegmentsOpen(false)}>
            <span css={overlayCloseXStyles}>
              <img src="/close-icon.svg" alt="Close" />
            </span>
            <span css={overlayCloseEscStyles}>esc</span>
          </button>

          <h1 css={overlayTitleStyles}>Choose segments</h1>

          <div css={overlayContentStyles}>
            <div css={overlayFieldGroupStyles}>
              <p css={overlayFieldLabelStyles}>Include</p>
              <div css={overlayInputWrapperStyles} ref={includeRef}>
                {selectedSegments.length === 0 ? (
                  <div css={overlayInputStyles} data-focused={includeDropdownOpen}>
                    <input
                      type="text"
                      placeholder="Start typing to find a segment"
                      onFocus={() => setIncludeDropdownOpen(true)}
                    />
                    <span css={chevronStyles}>
                      <img src="/chevron-down.svg" alt="" />
                    </span>
                  </div>
                ) : (
                  <div
                    css={overlayInputSelectedStyles}
                    data-focused={includeDropdownOpen}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') setIncludeDropdownOpen(!includeDropdownOpen);
                    }}
                    onClick={() => setIncludeDropdownOpen(!includeDropdownOpen)}
                  >
                    <div css={selectedBadgesStyles}>
                      {selectedSegments.map(name => {
                        const seg = SEGMENTS.find(s => s.name === name);
                        return (
                          <span
                            key={name}
                            css={selectedBadgeStyles}
                            style={{ background: seg?.color }}
                          >
                            {name}
                            <span
                              css={badgeRemoveStyles}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.stopPropagation();
                                  setSelectedSegments(selectedSegments.filter(s => s !== name));
                                }
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                setSelectedSegments(selectedSegments.filter(s => s !== name));
                              }}
                            >
                              <img src="/badge-close.svg" alt="Remove" />
                            </span>
                          </span>
                        );
                      })}
                    </div>
                    <div css={includeFieldRightStyles}>
                      <button
                        type="button"
                        css={clearButtonStyles}
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedSegments([]);
                        }}
                      >
                        Clear
                      </button>
                      <span css={chevronStyles}>
                        <img src="/chevron-down.svg" alt="" />
                      </span>
                    </div>
                  </div>
                )}
                {includeDropdownOpen && (
                  <div css={segmentDropdownStyles}>
                    {SEGMENTS.filter(seg => !selectedSegments.includes(seg.name)).map(seg => (
                      <div
                        key={seg.name}
                        css={segmentRowStyles}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            setSelectedSegments([...selectedSegments, seg.name]);
                            setIncludeDropdownOpen(false);
                          }
                        }}
                        onClick={() => {
                          setSelectedSegments([...selectedSegments, seg.name]);
                          setIncludeDropdownOpen(false);
                        }}
                      >
                        <span css={segmentBadgeStyles} style={{ background: seg.color }}>
                          {seg.name}
                        </span>
                        <span css={segmentCountStyles}>{seg.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div css={overlayExcludeGroupStyles}>
              <div css={overlayFieldGroupStyles}>
                <p css={overlayFieldLabelStyles}>Exclude</p>
                <div css={overlayInputStyles}>
                  <input type="text" placeholder="Start typing to find a segment" />
                  <span css={chevronStyles}>
                    <img src="/chevron-down.svg" alt="" />
                  </span>
                </div>
              </div>
              <p css={overlayHelperStyles}>Excluded segments override included segments</p>
            </div>

            <button type="button" css={overlaySaveStyles} onClick={() => setSegmentsOpen(false)}>
              Save
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modals */}
      {confirmModal && (
        <div css={modalBackdropStyles}>
          {confirmModal === 'check' && (
            <div css={modalCardStyles}>
              <h2 css={modalTitleStyles}>Let&apos;s double check!</h2>
              <div css={modalBodyStyles}>
                <p css={modalBodyBoldStyles}>Your about to test 2 email variants</p>
                <p>
                  Each will be sent immediately to 120 recipients. The winning version will be
                  automatically sent to the remaining 480 recipients.
                </p>
              </div>
              <div css={modalActionsStyles}>
                <button
                  type="button"
                  css={modalPrimaryStyles}
                  onClick={() => setConfirmModal('woohoo')}
                >
                  Send now
                </button>
                <button
                  type="button"
                  css={modalSecondaryStyles}
                  onClick={() => setConfirmModal(null)}
                >
                  or cancel &amp; go back
                </button>
              </div>
            </div>
          )}
          {confirmModal === 'woohoo' && (
            <div css={modalCardStyles}>
              <h2 css={modalTitleStyles}>Woohoo!</h2>
              <p css={modalBodyStyles}>You did it! Your test is live.</p>
              <div css={modalActionsStyles}>
                <button
                  type="button"
                  css={modalPrimaryStyles}
                  onClick={() => history.push('/ab-results')}
                >
                  View test progress
                </button>
                <button
                  type="button"
                  css={modalSecondaryStyles}
                  onClick={() => setConfirmModal(null)}
                >
                  Oops! Undo my send quick
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
